// import { captureException } from '@sentry/nextjs'
import { useAtom } from 'jotai'
import { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { VideoData } from '../../../../interfaces'
import { getTimeRemaining } from '../../../../utils/time'
import useAtomWithRef from '../../../../utils/useAtomWithRef'
import useInterval from '../../../../utils/useInterval'
import {
  controlsVisibilityLockAtom,
  fullscreenAtom,
  ownsNFTAtom,
  rControlsVisibilityAtom,
  videoElementAtom,
  wControlsVisibilityAtom,
  wVisibilityTimerIdAtom,
} from '../atoms'
import BigPlayOverlay from '../BigPlayOverlay'
import FullscreenToggle from '../FullscreenToggle'
import LockDisplay from '../LockDisplay'
import PlayPauseButton from '../PlayPauseButton'
import PreviewText from '../PreviewText'
import RemainingTimeDisplay from '../RemainingTimeDisplay'
import Timeline from '../Timeline'
import ClickRecognizer from './ClickRecognizer'
import KeyHandler from './KeyHandler'
import VolumeSlider from './VolumeSlider'
// import SettingsButton from './SettingsButton'
// import SkipButton from './SkipButton'

/*======================== React component ===================================*/

const WebControls = ({
  video,
  scheduledTime,
}: {
  video: VideoData
  scheduledTime: Date
}): JSX.Element => {
  const [controlsVisible] = useAtom(rControlsVisibilityAtom)
  const ref = useRef<HTMLDivElement>(null)
  const [, setControlsVisible] = useAtom(wControlsVisibilityAtom)
  const [controlsVisibleRef] = useAtomWithRef(rControlsVisibilityAtom)
  const [ownsNFT] = useAtom(ownsNFTAtom)
  const [showBigPlay, setShowBigPlay] = useState(true)
  const [, setControlsVisibilityLocked] = useAtom(controlsVisibilityLockAtom)

  const isMouseOverInitiallyRef = useRef(false)

  const [isFullscreenRef, isFullscreen] = useAtomWithRef(fullscreenAtom)

  const [videoElementRef, videoElement] = useAtomWithRef(videoElementAtom)

  const [, setVisibilityTimerId] = useAtom(wVisibilityTimerIdAtom)

  const bottomColumnRef = useRef<HTMLDivElement>(null)
  const clickableAreaRef = useRef<HTMLDivElement>(null)

  // We need to prevent the pointer lock when the mouse is outside the player,
  // so whenever a "mouseleave" event is fired.
  const [preventPointerLock, setPreventPointerLock] = useState(false)
  const [live, setIsLive] = useState(scheduledTime <= new Date())

  useInterval(() => {
    const time = getTimeRemaining(scheduledTime)
    if (time.days <= 0 && time.hours <= 0 && time.minutes <= 0 && time.seconds <= 0) {
      setIsLive(true)
    } else {
      setIsLive(false)
    }
  }, 1000)

  // It's important that the user sees whether the video is a trailer or a
  // preview. That's why we show the controls for a few seconds after the
  // video has started playing.
  useEffect(() => {
    setControlsVisible(true)
    setControlsVisibilityLocked(true)
  }, [])

  useEffect(() => {
    // The locked audio indicates that we are only allowed to play the video
    // in muted mode. If that's the case, we don't wanna autoplay the video
    // at all.
    // isAudioLocked().then((locked) => {
    //   setShowBigPlay(locked)
    // })
    if (!videoElement) return

    videoElement.addEventListener(
      'playing',
      () => {
        setShowBigPlay(false)
        setControlsVisibilityLocked(false)
        if (!isMouseOverInitiallyRef.current) {
          setVisibilityTimerId('start')
        }
      },
      { once: true }
    )

    videoElement.addEventListener('play', () => {
      setShowBigPlay(false)
      setControlsVisibilityLocked(false)

      if (!isFullscreenRef.current) return

      if (controlsVisibleRef.current) {
        // Creates a new timer that will hide the controls after three seconds.
        // The obtained timer id is stored in the visibilityTimerAtom atom.
        setVisibilityTimerId('start')
      }
    })

    videoElement.addEventListener('pause', () => {
      setShowBigPlay(true)
      setControlsVisible(true)
      setControlsVisibilityLocked(true)

      if (!isFullscreenRef.current) return

      // Cancel the timer, as the controls should not hide automatically when
      // the video is paused.
      setVisibilityTimerId('cancel')
    })
  }, [videoElement])

  // When the visibility of the controls changes, then we need to start a timer
  // which will hide the controls again.
  useEffect(() => {
    if (!isFullscreenRef.current) return

    // When the controls are hidden manually, cancel any timer which hides them
    // again, as it's useless.
    // Or when the controls get hidden again, then clear the timer id stored in
    // the visibilityTimerAtom atom.
    if (!controlsVisible) {
      setVisibilityTimerId('cancel')
    }

    // When the player isn't paused and the controls are visible, then we need
    // to start a timer which will hide the controls again.
    const isVideoPlaying = videoElementRef.current ? !videoElementRef.current.paused : false
    if (controlsVisible && isVideoPlaying) {
      setVisibilityTimerId('start')
    }
  }, [controlsVisible])

  useEffect(() => {
    if (!clickableAreaRef.current || !bottomColumnRef.current) return

    clickableAreaRef.current.addEventListener('mousemove', () => {
      if (!isFullscreenRef.current) return

      setControlsVisible(true)
      // Cancel the timer, as the user is interacting with the controls.
      setVisibilityTimerId('restart')
    })

    bottomColumnRef.current.addEventListener('mousemove', () => {
      if (!isFullscreenRef.current) return

      setControlsVisible(true)
      // Cancel the timer, as the user is interacting with the controls.
      setVisibilityTimerId('cancel')
    })
  }, [clickableAreaRef.current, bottomColumnRef.current])

  useEffect(() => {
    if (preventPointerLock) return

    if (isFullscreen && !controlsVisible) {
      clickableAreaRef.current!.requestPointerLock()
    } else if (isFullscreen && controlsVisible) {
      document.exitPointerLock()
    }
  }, [isFullscreen, controlsVisible])

  useEffect(() => {
    if (!ref.current) {
      return
    }

    ref.current.addEventListener('mouseenter', () => {
      if (isFullscreenRef.current) {
        setPreventPointerLock(false)
        setVisibilityTimerId('restart')
      }

      setControlsVisible(true)
    })

    ref.current.onmouseover = () => {
      isMouseOverInitiallyRef.current = true
    }

    ref.current.addEventListener('mouseleave', () => {
      if (isFullscreenRef.current) {
        setPreventPointerLock(true)
        setVisibilityTimerId('cancel')
      }

      console.log('Hide')

      setControlsVisible(false)
    })
  }, [ref.current])

  return (
    <>
      {showBigPlay && <BigPlayOverlay />}
      <Wrapper ref={ref} visible={controlsVisible}>
        <ClickRecognizer ref={clickableAreaRef} />
        <BottomColumn ref={bottomColumnRef}>
          <TimelineRow>
            {!ownsNFT && (video.gated || !live) && (
              <PreviewPositon>
                <PreviewText />
              </PreviewPositon>
            )}
            <Timeline />
            <RemainingTimeDisplay />
          </TimelineRow>
          <ControlBar>
            <SizedBox width={DEFAULT_ICONS_HEIGHT}>
              <PlayPauseButton />
            </SizedBox>
            <Spacer width={DEFAULT_SPACE_BETWEEN_ICONS} />
            <VolumeSlider />
            <Margin margin="0 0 0 auto">
              <SizedBox width={DEFAULT_ICONS_HEIGHT}>
                <FullscreenToggle />
              </SizedBox>
            </Margin>
          </ControlBar>
        </BottomColumn>
      </Wrapper>
      <KeyHandler />
      {(video.gated || !live) && (
        <LeftBottomCornerPositioned>
          <LockDisplay />
        </LeftBottomCornerPositioned>
      )}
    </>
  )
}

export default WebControls

/*================================ Constants =================================*/

const DEFAULT_ICONS_HEIGHT = '25px'

const DEFAULT_SPACE_BETWEEN_ICONS = '20px'

/*======================== Styled components =================================*/

const LeftBottomCornerPositioned = styled.div`
  position: absolute;
  bottom: 25px;
  left: 25px;
`

const Wrapper = styled.div<{ visible: boolean }>`
  width: 100%;
  height: 100%;
  position: absolute;
  opacity: ${({ visible }) => (visible ? 1 : 0)};
  transition: opacity 300ms ease-out;
`

const ControlBar = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  padding-top: 10px;
  padding-bottom: 15px;
`

const BottomColumn = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  margin-left: 30px;
  margin-right: 30px;
`

const TimelineRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  height: 20px;
  gap: 15px;
`

const Spacer = styled.div<{ width: string }>`
  width: ${({ width }) => width};
  height: 20px;
`

const Margin = styled.div<{ margin: string }>`
  margin: ${({ margin }) => margin};
`

const SizedBox = styled.div<{ width: string }>`
  width: ${({ width }) => width};
`

const PreviewPositon = styled.div`
  position: absolute;
  top: -27px;
  left: 0;
`

// const isAudioLocked = (): Promise<boolean> => {
//   const window: any = globalThis.window

//   return new Promise<boolean>((resolve) => {
//     const checkHTML5Audio = async () => {
//       const audio = new Audio()
//       try {
//         audio.play()
//         resolve(false)
//       } catch (err) {
//         resolve(true)
//         captureException(err)
//       }
//     }
//     try {
//       const context = new (window.AudioContext || window.webkitAudioContext)()
//       resolve(context.state === 'suspended')
//     } catch (e) {
//       checkHTML5Audio()
//       captureException(e)
//     }
//   })
// }
